import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import * as reportGeneration from '../../../../../redux/reportGeneration';
import { BUTTON_SIZE, BUTTON_TYPE, FeatureFlagRequired, Icon, icons, NavigationButton } from '../../../../generic';
import Button from '../../../../generic/Button';
import type { ProjectContext, ProjectGroupContext, ReportCategory } from '../../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from '../shared/types';
import { getDownloadURL } from '../projectLevel/utils';
import { getDownloadVulnerabilityReportURL } from '../releaseGroupLevel/utils';
import { useToggle } from '../../../../../hooks';
import EmailDialog from './EmailDialog';
import PublishReportButton from './PublishReportButton';
import PublicLinkButton from './PublicLinkButton';
import Flex from '../../../../generic/Flex';
import { isFreeTier, PREMIUM_COMPLIANCE_PLAN_NAME } from '../../../../../../shared/subscriptions';
import { window } from '../../../../../globals';
import PaidFeatureGate from '../../../../generic/Gates/PaidFeatureGate';
import { Action, ResourceType } from '../../../../../../typings/permissions';
import { SBOM_PORTAL } from '../../../../../../shared/featureFlags/flagNames';

type Props = {
  isDisabled?: boolean;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
};

const ActionButtons: VoidFunctionComponent<Props> = ({
  format,
  reportType,
  includeOptions,
  dependencyMetadataOptions,
  isDisabled,
}: Props) => {
  const isVulnerabilityReport = reportType === 'vulnerability';
  const hasPermissionToEditPortalSettings = window.hasPermission({
    resourceType: ResourceType.OrganizationPortalReports,
    action: Action.Manage,
  });
  const isPaidFeatureGateRestricted = isFreeTier(window?.user?.organization?.stripe_plan);
  const projectType = useSelector(reportGeneration.selectors.getProjectType);
  const reportContext = useSelector(reportGeneration.selectors.getReportContext);
  const [isToggled, onToggle] = useToggle();

  return projectType === 'project' ? (
    <Flex gap="4px">
      {!isVulnerabilityReport && (
        <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill planToShow={PREMIUM_COMPLIANCE_PLAN_NAME}>
          <PublicLinkButton
            isDisabled={isDisabled}
            format={format}
            reportType={reportType}
            includeOptions={includeOptions}
            dependencyMetadataOptions={dependencyMetadataOptions}
          />
        </PaidFeatureGate>
      )}
      <NavigationButton
        isDisabled={isDisabled}
        openInNewTab
        size={BUTTON_SIZE.LARGE}
        to={
          getDownloadURL({
            format,
            projectRevisionLocator: (reportContext as ProjectContext).revision,
            reportType,
            includeOptions,
            dependencyMetadataOptions,
          }) || ''
        }
      >
        <Icon icon={icons.downloadRegular} /> Download
      </NavigationButton>
    </Flex>
  ) : (
    <Flex gap="4px">
      {isToggled && (
        <EmailDialog
          onClose={onToggle}
          format={format}
          reportType={reportType}
          includeOptions={includeOptions}
          dependencyMetadataOptions={dependencyMetadataOptions}
        />
      )}
      {!isVulnerabilityReport && (
        <Button onClick={onToggle} size={BUTTON_SIZE.LARGE} type={BUTTON_TYPE.SECONDARY} isDisabled={isDisabled}>
          <Icon icon={icons.envelopeRegular} /> Email
        </Button>
      )}
      {
        isVulnerabilityReport && (
          <NavigationButton
            isDisabled={isDisabled}
            openInNewTab
            size={BUTTON_SIZE.LARGE}
            to={
              getDownloadVulnerabilityReportURL({
                groupId: (reportContext as ProjectGroupContext).groupId,
                releaseId: (reportContext as ProjectGroupContext).releaseId,
                includeOptions,
                dependencyMetadataOptions,
              }) || ''
            }
          >
            <Icon icon={icons.downloadRegular} /> Download
          </NavigationButton>
        )
      }
      <FeatureFlagRequired flagName={SBOM_PORTAL}>
        {hasPermissionToEditPortalSettings && (
          <PublishReportButton
            isDisabled={isDisabled}
            format={format}
            reportType={reportType}
            includeOptions={includeOptions}
            dependencyMetadataOptions={dependencyMetadataOptions}
          />
        )}
      </FeatureFlagRequired>
    </Flex>
  );
};

export default ActionButtons;
